import { WabunkaExperience } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

import { useAsyncFetchFromPublicWabunkaApi } from '@/composables/models/wabunka/useAsyncFetchFromPublicWabunkaApi';
import { isServerRendering } from '@/composables/utils/isServerRendering';
import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { useErrorCreator } from '@/composables/utils/wabunka/useErrorCreator';
import { createDefaultWabunkaExperienceViewModel, buildWabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';

/**
 * Wabunka体験情報の取得に使用する Composable
 * Page でのみ使用可能
 */
export const useAsyncWabunkaExperience = async (idOrSlug: Ref<string>) => {
  // コンテキストに依存する処理は await 前に行う
  const path = computed(() => `/experiences/${idOrSlug.value}`);
  const { selectedCurrency } = useCurrency();
  const { createWabunkaError } = useErrorCreator();

  const query = computed(() => ({
    includes: 'provider,interpreterOptions,experienceOptions,reservationQuestions,experienceImages',
    ...(selectedCurrency.value !== 'JPY' ? { currency: selectedCurrency.value } : {}),
  }));

  const { data, status } = await useAsyncFetchFromPublicWabunkaApi({
    path,
    query,
    options: { server: true },
    converter: WabunkaExperience.fromJSON,
  });

  // 「エラーなしで data が null」の異常応答を DRAFT デフォルトにフォールバックさせない（JCAT-5203）。
  // SSR でフォールバックすると公開中の体験ページが 200 + noindex,nofollow で配信され、
  // Google のインデックスから除外される事故につながる。
  // 503 であれば Google は一時障害として扱い、インデックスを維持したまま再クロールする。
  //
  // クライアント側では throw しない。エッジの通貨別キャッシュとクライアント通貨の不一致により
  // ハイドレーション時に payload を引き当てられず一時的に null になるケースが本番で常態的に
  // 発生しており（直後の再フェッチでデータは到着する）、throw するとエラーページになってしまう。
  // クライアント側の noindex 抑止はページ側の isExperienceMissing ガードで行う。
  if (isServerRendering() && data.value == null) {
    throw createWabunkaError({
      statusCode: 503,
      statusMessage: 'Service Unavailable',
      fatal: true,
      customMessage: `experience fetch returned null without error: ${idOrSlug.value}`,
    });
  }

  const experience = computed(() => (data.value ? buildWabunkaExperienceViewModel(data.value) : createDefaultWabunkaExperienceViewModel()));

  // 取得失敗（null）と取得済みデータを呼び出し側で区別するためのフラグ（JCAT-5203）。
  // null を DRAFT 扱いして noindex を出さないよう、SEO メタの判定にはこちらを併用する。
  const isExperienceMissing = computed(() => data.value == null);

  return { experience, isExperienceMissing, ...useAsyncDataRequestStatus(status) };
};
